import { useState, useEffect } from "react";
import {
  Box,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { getDisasterStatusCaches } from "../../../../../../api";
import type { DisasterStatusCacheItem } from "../../../../../../types/disaster";

type CacheProps = {
  statusId?: string | null;
  disasterId?: string | null;
  siteId?: number;
  refreshTrigger?: number;
};

type CacheColumn = {
  id: string;
  label: string;
  minWidth: number;
  isSmallScreen: boolean;
};

type CacheRow = DisasterStatusCacheItem & {
  update_at?: string;
  update_user?: { name?: string };
  damage_at?: string;
  damage_details?: string;
  sale_detail_status?: number;
  reasons_closing_status?: number;
  water_test_status?: number;
  reasons_closing?: string;
  not_damage?: boolean;
  personal_damage?: boolean;
  property_damage?: boolean;
  facility_damage?: boolean;
  is_electric?: boolean | string;
  recovery_status?: boolean | string;
};

const Cache = ({
  statusId: _statusId,
  disasterId,
  siteId,
  refreshTrigger,
}: CacheProps) => {
  const [historyCaches, setHistoryCaches] = useState<CacheRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCache, setSelectedCache] = useState<CacheRow | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // カラム定義
  const columns: CacheColumn[] = [
    { id: "update_at", label: "更新日時", minWidth: 120, isSmallScreen: true },
    { id: "update_user", label: "更新者", minWidth: 100, isSmallScreen: true },
    {
      id: "damage_at",
      label: "被害確認日時",
      minWidth: 120,
      isSmallScreen: false,
    },
    {
      id: "damage_status",
      label: "被害状況",
      minWidth: 120,
      isSmallScreen: true,
    },
    {
      id: "damage_details",
      label: "被害詳細",
      minWidth: 150,
      isSmallScreen: false,
    },
    {
      id: "sale_status",
      label: "営業状況",
      minWidth: 120,
      isSmallScreen: false,
    },
    {
      id: "reasons_closing_status",
      label: "休止要因",
      minWidth: 120,
      isSmallScreen: false,
    },
    {
      id: "water_test_status",
      label: "浸水・冠水時検水対応",
      minWidth: 150,
      isSmallScreen: false,
    },
    {
      id: "reasons_closing",
      label: "再開見込み",
      minWidth: 150,
      isSmallScreen: false,
    },
  ];

  // APIからデータを取得
  const fetchDisasterStatusCaches = async () => {
    if (!disasterId || !siteId) return;
    setLoading(true);
    try {
      const response = await getDisasterStatusCaches({
        disaster_id: Number(disasterId),
        site_id: siteId,
      });
      if (response.status !== "ok") {
        setError("データの取得に失敗しました");
        setLoading(false);
        return;
      }
      setHistoryCaches(
        (response.disaster_status_caches || []) as CacheRow[],
      );
      setLoading(false);
    } catch (error) {
      console.error("API呼び出しエラー", error);
      setError("データの取得に失敗しました");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disasterId && siteId) {
      fetchDisasterStatusCaches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disasterId, siteId, refreshTrigger]);

  // 日付を整形する関数
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}/${String(date.getDate()).padStart(2, "0")} ${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  // 日付（日付のみ）を整形する関数
  const formatDateDay = (dateString?: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}/${String(date.getDate()).padStart(2, "0")}`;
  };

  // 状態名を日本語で表示する関数
  const translateStatus = (status: string | Record<string, boolean> | null) => {
    if (!status) return "-";

    const statusMap: Record<string, string | Record<string, string>> = {
      // 被害状況
      not_damage: "被害なし",
      personal_damage: "人的被害",
      property_damage: "物的被害",
      facility_damage: "施設設備被害",

      // 営業状況
      sale_status: {
        open: "営業中",
        closed: "休業中",
        unknown: "不明",
      },

      // 営業詳細状況
      sale_detail_status: {
        all: "全商品",
        partial: "一部商品のみ",
        none: "販売なし",
        unknown: "不明",
      },

      // 休止要因状態
      reasons_closing_status: {
        true: "あり",
        false: "なし",
        unknown: "不明",
      },

      // 浸水・冠水時検水対応
      water_test_status: {
        true: "実施済",
        false: "未実施",
        unknown: "不明",
      },
    };

    // オブジェクト型の場合
    if (typeof status === "object") {
      return (
        Object.entries(status)
          .filter(([key, value]) => value === true)
          .map(([key]) => (statusMap[key] as string) || key)
          .join(", ") || "-"
      );
    }

    // sale_statusなどのEnum値の場合
    if (statusMap[status as string]) {
      const mapped = statusMap[status as string];
      return typeof mapped === "object"
        ? mapped[status as string] || (status as string)
        : mapped;
    }

    return String(status);
  };

  const getSaleStatusDetail = (cache: CacheRow) => {
    const salesStatusMap: Record<number, string> = {
      1: "平常営業",
      2: "時間短縮営業",
      3: "給油制限営業",
      4: "緊急車両限定営業",
      5: "営業停止（停電）",
      6: "営業停止（施設損壊）",
      7: "営業停止（その他）",
      0: "不明",
    };

    return salesStatusMap[cache.sale_detail_status ?? -1] || "-";
  };

  const getClosingReasons = (cache: CacheRow) => {
    const reasonsClosingMap: Record<number, string> = {
      1: "停電",
      2: "浸水・冠水",
      3: "設備被害",
      4: "人員被害",
      5: "油切れ",
      6: "計画休業",
    };

    const reasonsClosingText =
      reasonsClosingMap[cache.reasons_closing_status ?? -1] || "-";
    return reasonsClosingText;
  };

  const getWaterTestStatus = (cache: CacheRow) => {
    const waterTestStatusMap: Record<number, string> = {
      1: "実施済み",
      2: "業者依頼中",
      0: "未実施",
    };

    return waterTestStatusMap[cache.water_test_status ?? -1] || "-";
  };

  const getDamageStatus = (cache: CacheRow) => {
    return cache.not_damage
      ? "被害なし"
      : [
        cache.personal_damage ? "人的被害" : null,
        cache.property_damage ? "物的被害" : null,
        cache.facility_damage ? "施設設備被害" : null,
        cache.is_electric ? "電話・インターネット接続不良有" : null,
        cache.recovery_status
          ? "点検記録簿（SS復旧チェック）実施で異常無し"
          : null,
      ]
        .filter(Boolean)
        .join(", ") || "-";
  };

  // 行クリック時の処理（詳細ダイアログを開く）
  const handleRowClick = (cache: CacheRow) => {
    setSelectedCache(cache);
    setIsDialogOpen(true);
  };

  // ダイアログを閉じる
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedCache(null);
  };

  return (
    <Box
      className="cache-container"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        width: "100%",
        justifyContent: "center",
        borderRadius: 2,
        borderTop: 16,
        borderColor: "primary.main",
        boxShadow: 3,
        color: "outline.dark",
        backgroundColor: "#fff",
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          padding: "16px 40px 32px 40px",
          gap: "16px",
          "@media screen and (max-width:900px)": {
            padding: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 20,
            fontWeight: "bold",
            color: "primary.main",
            whiteSpace: "nowrap",
          }}
        >
          入力履歴
        </Box>
        <Divider />

        {loading ? (
          <Typography>読み込み中...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : historyCaches.length === 0 ? (
          <Typography>履歴データがありません</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader aria-label="履歴テーブル" size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      sx={{
                        minWidth: column.minWidth,
                        padding: "8px",
                        "@media screen and (max-width:900px)": {
                          display: column.isSmallScreen ? "table-cell" : "none",
                        },
                        fontWeight: "bold",
                        borderBottom: "2px solid #606060",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {historyCaches.map((cache, index) => (
                  <TableRow
                    key={index}
                    hover
                    onClick={() => handleRowClick(cache)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "table-cell",
                        },
                      }}
                    >
                      {formatDate(cache.update_at)}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "table-cell",
                        },
                      }}
                    >
                      {cache.update_user?.name || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "none",
                        },
                      }}
                    >
                      {formatDate(cache.damage_at)}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "table-cell",
                        },
                      }}
                    >
                      {getDamageStatus(cache)}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "none",
                        },
                      }}
                    >
                      {cache.damage_details || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "none",
                        },
                      }}
                    >
                      {getSaleStatusDetail(cache)}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "none",
                        },
                      }}
                    >
                      {getClosingReasons(cache)}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "none",
                        },
                      }}
                    >
                      {getWaterTestStatus(cache)}
                    </TableCell>
                    <TableCell
                      sx={{
                        "@media screen and (max-width:900px)": {
                          display: "none",
                        },
                      }}
                    >
                      {cache.reasons_closing || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* スマートフォン用詳細ダイアログ */}
        <Dialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
        >
          <Box
            sx={{
              display: "flex",
              height: "56px",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "2px solid #CCC",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                paddingLeft: "8px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              入力履歴詳細
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleDialogClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedCache && (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "40%" }}>
                    更新日時
                  </TableCell>
                  <TableCell>{formatDate(selectedCache.update_at)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>更新者</TableCell>
                  <TableCell>
                    {selectedCache.update_user?.name || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    被害確認日時
                  </TableCell>
                  <TableCell>{formatDate(selectedCache.damage_at)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>被害状況</TableCell>
                  <TableCell>{getDamageStatus(selectedCache)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>被害詳細</TableCell>
                  <TableCell>{selectedCache.damage_details || "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>営業状況</TableCell>
                  <TableCell>{getSaleStatusDetail(selectedCache)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>休止要因</TableCell>
                  <TableCell>{getClosingReasons(selectedCache)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    浸水・冠水時検水対応
                  </TableCell>
                  <TableCell>{getWaterTestStatus(selectedCache)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>再開見込み</TableCell>
                  <TableCell>{selectedCache.reasons_closing || "-"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Dialog>
      </Box>
    </Box>
  );
};

export default Cache;
